import { faBan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useNavigate } from "react-router-dom";
import { publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth.store";

interface Brand {
	id: string;
	name: string;
}

interface PostLoginLandingPageProps {
	scope: string;
	brands: Brand[];
}

interface AuthResponse {
	accessToken: string;
	expiresAt: string;
	userId: string;
	userName: string;
}

const PostLoginLandingPage: React.FC<PostLoginLandingPageProps> = ({
	scope,
	brands
}) => {
	const navigate = useNavigate();
	const { setAuth, setUser, setLogin, refreshToken } = useAuthStore();

	const handleLoginClick = async (
		isAdmin: boolean,
		brandId = "",
		brandName = ""
	) => {
		try {
			const tokenRes = await publicApiClient.post<AuthResponse>(
				resolveEndpoint(API_ENDPOINTS, ["AUTH", "REFRESH"]),
				{
					refreshToken,
					...(isAdmin ? {} : { brandId })
				}
			);

			const { accessToken, expiresAt, userId, userName } = tokenRes.data;

			if (!accessToken || !refreshToken || !expiresAt) {
				throw new Error("Invalid authentication response");
			}

			setAuth({ accessToken, refreshToken, expiresAt: expiresAt.toString() });
			setUser(userId, userName);
			setLogin({
				isBrand: (!isAdmin).toString(),
				isSuperAdmin: isAdmin.toString(),
				brandId: isAdmin ? "" : brandId,
				brandName: isAdmin ? "" : brandName
			});

			navigate(isAdmin ? "/admin/brands" : `/${brandId}/integrations`);
		} catch (error) {
			console.error("Login error:", error);
		}
	};

	return (
		<div className="bg-slate-50 flex flex-col justify-center px-10 shadow-2xl sm:mx-auto sm:w-full w-full py-8">
			<div className="flex-1 h-full">
				<div>
					<h2 className="mt-6 text-center text-4xl font-semibold tracking-tight text-blue-fynxt_2">
						WELCOME
					</h2>
					<p className="mt-2 text-center text-sm text-slate-600">
						Access your dashboard or select a brand.
					</p>
				</div>

				<div className="mt-8">
					{scope === "SYSTEM" && (
						<button
							type="button"
							onClick={() => handleLoginClick(true, "", "")}
							className="group relative flex w-full justify-center rounded-lg border border-transparent bg-blue-fynxt py-3 px-4 text-lg font-semibold text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors duration-150 ease-in-out"
						>
							Go to Admin Panel
						</button>
					)}
				</div>

				<div className="relative my-8">
					<div
						className="absolute inset-0 flex items-center"
						aria-hidden="true"
					>
						<div className="w-full border-t border-slate-300" />
					</div>
					<div className="relative flex justify-center">
						<span className="bg-slate-50 px-3 text-lg font-medium text-slate-700">
							Brands
						</span>
					</div>
				</div>

				<div className="overflow-y-auto space-y-4 xl:max-h-[560px]">
					{brands.length > 0 ? (
						brands.map((brand) => (
							<button
								type="button"
								key={brand.id}
								onClick={() => handleLoginClick(false, brand.id, brand.name)}
								className="border-slate-300 w-full p-4 text-slate-700 bg-white rounded-lg shadow-md hover:shadow-lg hover:bg-sky-50 transition-all duration-150 ease-in-out focus:outline-none"
							>
								<span className="font-semibold text-lg text-blue-fynxt_2">
									{brand.name}
								</span>
							</button>
						))
					) : (
						<div className="flex items-center justify-center w-full p-4 text-gray-500 bg-gray-100 rounded-lg space-x-2 block w-full p-4 text-slate-700 bg-white rounded-lg border">
							<FontAwesomeIcon
								icon={faBan}
								className="text-[14px] text-[#0099FF]"
							/>
							<span>No Brands Available</span>
						</div>
					)}
				</div>
			</div>
			<p className="pt-6 text-center text-slate-500 text-xs">
				&copy; {new Date().getFullYear()} FYNXT. All rights reserved.
			</p>
		</div>
	);
};

export default PostLoginLandingPage;
